import * as t from "io-ts";

import type { Either } from "@ps-bridge/data/either";
import type {
  ListingInferenceAgentRequestId,
  ListingInferenceAgentTraceId,
  ListingInferenceClaimId,
  ListingInferenceClaimKind,
  ListingInferenceDecisionReason,
  ListingInferenceDisposition,
  ListingInferenceListingId,
  ListingInferenceReviewer,
  ListingInferenceSourceRef,
} from "@ps-bridge/models/brands/listingInferenceReviewPortal";
import {
  makeListingInferenceAgentRequestId,
  makeListingInferenceAgentTraceId,
  makeListingInferenceClaimId,
  makeListingInferenceClaimKind,
  makeListingInferenceDecisionReason,
  makeListingInferenceDisposition,
  makeListingInferenceListingId,
  makeListingInferenceReviewer,
  makeListingInferenceSourceRef,
} from "@ps-bridge/models/brands/listingInferenceReviewPortal";
import type { PSEither } from "../purescript/interop";

type BrandConstructor<Brand> = (value: string) => Either<string, Brand>;

const brandCodec = <Brand extends string>(
  name: string,
  construct: BrandConstructor<Brand>,
): t.Type<Brand, string, string> =>
  new t.Type<Brand, string, string>(
    name,
    (value): value is Brand => typeof value === "string",
    (value, context) => {
      if (typeof value !== "string") {
        return t.failure(value, context, `${name} must be a string`);
      }
      const validation = construct(value) as never as PSEither<string, Brand>;
      if (validation.constructor.name === "Right") {
        return t.success(value.trim() as Brand);
      }
      return t.failure(value, context, validation.value0);
    },
    (value) => value,
  );

export const listingInferenceListingIdCodec = brandCodec<ListingInferenceListingId>(
  "ListingInferenceListingId",
  makeListingInferenceListingId,
);

export const listingInferenceClaimIdCodec = brandCodec<ListingInferenceClaimId>(
  "ListingInferenceClaimId",
  makeListingInferenceClaimId,
);

export const listingInferenceClaimKindCodec = brandCodec<ListingInferenceClaimKind>(
  "ListingInferenceClaimKind",
  makeListingInferenceClaimKind,
);

export const listingInferenceDispositionCodec = brandCodec<ListingInferenceDisposition>(
  "ListingInferenceDisposition",
  makeListingInferenceDisposition,
);

export const listingInferenceReviewerCodec = brandCodec<ListingInferenceReviewer>(
  "ListingInferenceReviewer",
  makeListingInferenceReviewer,
);

export const listingInferenceDecisionReasonCodec = brandCodec<ListingInferenceDecisionReason>(
  "ListingInferenceDecisionReason",
  makeListingInferenceDecisionReason,
);

export const listingInferenceSourceRefCodec = brandCodec<ListingInferenceSourceRef>(
  "ListingInferenceSourceRef",
  makeListingInferenceSourceRef,
);

export const listingInferenceAgentRequestIdCodec = brandCodec<ListingInferenceAgentRequestId>(
  "ListingInferenceAgentRequestId",
  makeListingInferenceAgentRequestId,
);

export const listingInferenceAgentTraceIdCodec = brandCodec<ListingInferenceAgentTraceId>(
  "ListingInferenceAgentTraceId",
  makeListingInferenceAgentTraceId,
);
